import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileUpload,
  faStar,
  faLightbulb,
  faGraduationCap,
  faMagic,
  faShareAlt,
  faRedo,
  faCheckCircle,
  faLayerGroup,
  faBrain,
  faClock,
  faPlay,
  faBolt
} from "@fortawesome/free-solid-svg-icons";
import {
  faWhatsapp,
  faFacebook,
  faLinkedin,
} from "@fortawesome/free-brands-svg-icons";
import confetti from "canvas-confetti";
import toast from "react-hot-toast";
import ApiServices from "../../Services/ApiServices";
import MiniLoader from "../../Shared/MiniLoader";
import FileUploader from "../../Shared/FileUploader";
import botImage from "../../../assets/images/bot.png";
import { useNavigate } from "react-router-dom";
import { storageHelper } from "../../../Helpers/storageHelper";
import Header from "../../Shared/Header";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const ZatounaDashboard = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [showAd, setShowAd] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowAd(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const userRole = storageHelper.getItem("user_type");
    if (userRole !== "User") {
      toast.error("عذراً، هذه الصفحة متاحة للطلاب فقط");
      navigate("/");
    }
  }, [navigate]);

  const validateAndSetFile = (selectedFile) => {
    if (selectedFile.type !== "application/pdf") {
      toast.error("يرجى اختيار ملف PDF فقط");
      return;
    }
    setFile(selectedFile);
    setPreviewUrl("pdf-placeholder");
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("يرجى اختيار ملف PDF أولاً");
      return;
    }
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("PDfFile", file);
      const response = await ApiServices.post("Student/Zatouna", formData, true, 200000);
      if (response) {
        setSummaryData(response);
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ["#6366f1", "#0f172a", "#fbbf24"],
        });
        toast.success("تم تلخيص الملف بنجاح!");
      }
    } catch (error) {
      toast.error(error || "حدث خطأ أثناء معالجة الملف");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardFlip = (index) => {
    setFlippedCards((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) newSet.delete(index);
      else newSet.add(index);
      return newSet;
    });
  };

  const handleShare = (platform) => {
    if (!summaryData) return;
    const keyPointsList = summaryData.key_points?.map((point, index) => `${index + 1}. ${point}`).join("\n") || "";
    const shareText = `تم إنشاء ملخص ذكي بواسطة الزتونة - منصة امتحاني\n\nالعنوان: ${summaryData.summary_title}\n\nالنقاط الرئيسية:\n${keyPointsList}\n\n#الزتونة #امتحاني`;
    const currentUrl = window.location.href;
    let shareUrl = "";
    if (platform === "whatsapp") shareUrl = `https://wa.me/?text=${encodeURIComponent(shareText + "\n\n" + currentUrl)}`;
    else if (platform === "facebook") shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
    else if (platform === "linkedin") shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`;
    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  return (
    <div className="zt-page">
      <Header />
      <br />

      <section className="zt-root" id="zatouna">
        <div className="zt-container">
          {/* ── header ── */}
          <motion.div
            className="zt-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div className="zt-eyebrow" variants={fadeUp} custom={0}>
              <span className="zt-eyebrow-dot" />
              أداة الزتونة الذكية
            </motion.div>
            <motion.h2 className="zt-title" variants={fadeUp} custom={1}>
              حول ملفاتك إلى <em>خلاصة تفاعلية</em>
            </motion.h2>
            <motion.p className="zt-subtitle" variants={fadeUp} custom={2}>
              ارفع ملفات الـ PDF الخاصة بك ودع رفيق يلخصها لك في نقاط ذكية وبطاقات تعليمية سهلة الحفظ
            </motion.p>
          </motion.div>

          <AnimatePresence mode="wait">
            {!summaryData ? (
              <motion.div
                key="upload"
                className="zt-upload-view"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                <div className="zt-hero-card">
                  <div className="zt-hero-content">
                    <div className="zt-bot-box">
                      <motion.img
                        src={botImage}
                        alt="Rafiq"
                        animate={isLoading ? { scale: [1, 1.05, 1], filter: ["blur(0px)", "blur(1px)", "blur(0px)"] } : { y: [0, -10, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className={isLoading ? 'thinking' : ''}
                      />
                      {isLoading && <div className="zt-thinking-pills"><span>رفيق يفكر</span><div className="dots"><span /><span /><span /></div></div>}
                    </div>
                    <div className="zt-hero-text">
                      <h3>أهلاً بك في الزتونة!</h3>
                      <p>سأقوم بتحليل ملفك التعليمي واستخراج أهم النقاط منه في ثوانٍ معدودة.</p>
                      <div className="zt-feat-pills">
                        <div className="zt-pill"><FontAwesomeIcon icon={faBrain} /> تلخيص ذكي</div>
                        <div className="zt-pill"><FontAwesomeIcon icon={faLayerGroup} /> بطاقات ذكية</div>
                        <div className="zt-pill"><FontAwesomeIcon icon={faBolt} /> نتائج فورية</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="zt-uploader-box">
                  <FileUploader
                    previewUrl={previewUrl}
                    onFileChange={(e) => validateAndSetFile(e.target.files[0])}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                    onDrop={(e) => { e.preventDefault(); setIsDragging(false); validateAndSetFile(e.dataTransfer.files[0]); }}
                    imageDragOver={isDragging}
                    accept="application/pdf"
                    placeholder="اسحب ملف الـ PDF هنا"
                    subText="أو اضغط لاختيار ملف (الحد الأقصى 20 صفحة)"
                    buttonText="اختيار ملف"
                    fileName={file?.name}
                    id="zt-pdf-upload"
                  />
                  {file && !isLoading && (
                    <motion.button className="zt-main-btn" onClick={handleUpload} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <FontAwesomeIcon icon={faMagic} />
                      ابدأ التلخيص الآن
                    </motion.button>
                  )}
                  {isLoading && (
                    <div className="zt-loader-wrap">
                      <MiniLoader size={30} color="#6366f1" />
                      <span>جاري معالجة الملف...</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="results"
                className="zt-results-view"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="zt-summary-panel">
                  <div className="zt-panel-header">
                    <FontAwesomeIcon icon={faLightbulb} />
                    <h2>{summaryData.summary_title || "ملخص المحتوى"}</h2>
                    <button className="zt-reset-btn" onClick={() => setSummaryData(null)}>
                      <FontAwesomeIcon icon={faRedo} /> ملف جديد
                    </button>
                  </div>
                  <div className="zt-points-grid">
                    {summaryData.key_points?.map((point, i) => (
                      <motion.div key={i} className="zt-point-card" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}>
                        <span className="num">{i + 1}</span>
                        <p>{point}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="zt-flashcards-section">
                  <div className="zt-section-title">
                    <FontAwesomeIcon icon={faLayerGroup} />
                    <h3>البطاقات التعليمية</h3>
                    <p>اضغط على البطاقة لعرض الإجابة</p>
                  </div>
                  <div className="zt-flashcards-grid">
                    {summaryData.flashcards?.map((card, idx) => (
                      <div key={idx} className={`zt-f-card ${flippedCards.has(idx) ? 'flipped' : ''}`} onClick={() => handleCardFlip(idx)}>
                        <div className="zt-f-front">
                          <div className="badge">سؤال</div>
                          <p>{card.question}</p>
                          <div className="hint"><FontAwesomeIcon icon={faMagic} /> اضغط للقلب</div>
                        </div>
                        <div className="zt-f-back">
                          <div className="badge">إجابة</div>
                          <p>{card.answer}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="zt-share-actions">
                  <div className="lab">شارك الفائدة مع أصدقائك:</div>
                  <div className="btns">
                    <button className="sh-whatsapp" onClick={() => handleShare("whatsapp")}><FontAwesomeIcon icon={faWhatsapp} /> واتساب</button>
                    <button className="sh-facebook" onClick={() => handleShare("facebook")}><FontAwesomeIcon icon={faFacebook} /> فيسبوك</button>
                    <button className="sh-linkedin" onClick={() => handleShare("linkedin")}><FontAwesomeIcon icon={faLinkedin} /> لينكد إن</button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <style>{`
        .zt-page { background: #fff; min-height: 100vh; }
        .zt-root {
          font-family: "Zain", sans-serif; direction: rtl;
          padding: 80px 0 100px; isolation: isolate;
        }
        .zt-container { max-width: 1260px; margin: 0 auto; padding: 0 40px; }

        /* Header */
        .zt-header { text-align: center; margin-bottom: 60px; }
        .zt-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: #6366f1; font-weight: 800; font-size: 0.82rem;
          letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 22px;
        }
        .zt-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
          animation: zt-pulse 2.2s ease-in-out infinite;
        }
        @keyframes zt-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.45); }
          50%{ box-shadow: 0 0 0 9px rgba(232,146,10,0); }
        }
        .zt-title { font-size: clamp(2.2rem, 4vw, 3.6rem); font-weight: 900; color: #0f172a; margin: 0 0 18px; line-height: 1.2; }
        .zt-title em { font-style: normal; background: linear-gradient(90deg, #6366f1 0%, #6366f1 55%, #6366f1 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .zt-subtitle { font-size: 1.08rem; color: #64748b; line-height: 1.85; max-width: 700px; margin: 0 auto; }

        /* Hero Card */
        .zt-hero-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 40px; padding: 60px; margin-bottom: 40px; position: relative; overflow: hidden; color: #0f172a; }
        .zt-hero-content { position: relative; z-index: 2; display: flex; align-items: center; gap: 50px; }
        .zt-bot-box { position: relative; text-align: center; }
        .zt-bot-box img { width: 140px; height: 140px; border-radius: 50%; background: rgba(255,255,255,0.05); padding: 15px; }
        .zt-thinking-pills { position: absolute; bottom: -20px; left: 50%; transform: translateX(-50%); background: #6366f1; padding: 6px 15px; border-radius: 100px; font-size: 0.8rem; font-weight: 900; white-space: nowrap; display: flex; align-items: center; gap: 8px; box-shadow: 0 10px 20px rgba(0,0,0,0.2); }
        .zt-thinking-pills .dots { display: flex; gap: 3px; }
        .zt-thinking-pills .dots span { width: 4px; height: 4px; background: #fff; border-radius: 50%; animation: zt-bounce 1s infinite; }
        .zt-thinking-pills .dots span:nth-child(2) { animation-delay: 0.2s; }
        .zt-thinking-pills .dots span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes zt-bounce { 0%,100%{ transform: translateY(0); } 50%{ transform: translateY(-5px); } }
        
        .zt-hero-text h3 { font-size: 2rem; font-weight: 900; margin-bottom: 15px; }
        .zt-hero-text p { font-size: 1.1rem; color: #94a3b8; line-height: 1.7; margin-bottom: 30px; }
        .zt-feat-pills { display: flex; gap: 12px; }
        .zt-pill { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); padding: 8px 18px; border-radius: 100px; font-size: 0.85rem; font-weight: 800; display: flex; align-items: center; gap: 10px; color: #cbd5e1; }

        /* Uploader */
        .zt-uploader-box { background: #fff; border: 1px solid #f1f5f9; border-radius: 40px; padding: 50px; text-align: center; box-shadow: 0 20px 50px rgba(0,0,0,0.02); }
        .zt-main-btn { margin-top: 40px; width: 100%; max-width: 400px; background: #6366f1; color: #fff; border: none; padding: 18px; border-radius: 20px; font-weight: 900; font-family: inherit; font-size: 1.2rem; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 15px; box-shadow: 0 15px 30px rgba(99,102,241,0.25); }
        .zt-loader-wrap { margin-top: 30px; display: flex; flex-direction: column; align-items: center; gap: 15px; color: #64748b; font-weight: 800; }

        /* Results */
        .zt-summary-panel { background: #f8fafc; border: 1px solid #f1f5f9; border-radius: 40px; padding: 50px; margin-bottom: 60px; }
        .zt-panel-header { display: flex; align-items: center; gap: 15px; margin-bottom: 35px; }
        .zt-panel-header h2 { font-size: 1.8rem; font-weight: 900; color: #0f172a; flex: 1; margin: 0; }
        .zt-panel-header i { color: #6366f1; font-size: 1.6rem; }
        .zt-reset-btn { background: #fff; border: 1px solid #e2e8f0; padding: 10px 20px; border-radius: 12px; font-family: inherit; font-weight: 800; color: #475569; cursor: pointer; transition: 0.2s; display: flex; align-items: center; gap: 10px; }
        .zt-reset-btn:hover { background: #f1f5f9; color: #0f172a; }
        .zt-points-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
        .zt-point-card { background: #fff; border-radius: 24px; padding: 25px; border: 1px solid #f1f5f9; display: flex; gap: 20px; }
        .zt-point-card .num { width: 32px; height: 32px; background: #6366f1; color: #fff; border-radius: 100px; display: flex; align-items: center; justify-content: center; font-weight: 900; flex-shrink: 0; }
        .zt-point-card p { font-size: 1rem; color: #475569; line-height: 1.7; margin: 0; font-weight: 700; }

        /* Flashcards */
        .zt-flashcards-section { margin-bottom: 60px; }
        .zt-section-title { text-align: center; margin-bottom: 40px; }
        .zt-section-title h3 { font-size: 2rem; font-weight: 900; color: #0f172a; margin: 0 0 10px; }
        .zt-section-title p { color: #64748b; font-weight: 700; }
        .zt-section-title i { color: #6366f1; font-size: 1.6rem; margin-bottom: 10px; }
        .zt-flashcards-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; perspective: 1000px; }
        .zt-f-card { height: 250px; position: relative; transform-style: preserve-3d; transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1); cursor: pointer; }
        .zt-f-card.flipped { transform: rotateY(180deg); }
        .zt-f-front, .zt-f-back { position: absolute; inset: 0; backface-visibility: hidden; border-radius: 32px; padding: 40px; display: flex; flex-direction: column; justify-content: center; align-items: center; text-align: center; border: 1px solid #f1f5f9; box-shadow: 0 10px 30px rgba(0,0,0,0.03); }
        .zt-f-front { background: #fff; }
        .zt-f-back { background: #f8fafc; color: #0f172a; transform: rotateY(180deg); border: 1px solid #6366f1; }
        .zt-f-card .badge { position: absolute; top: 25px; right: 30px; font-size: 0.75rem; font-weight: 900; background: #f1f5f9; padding: 4px 12px; border-radius: 8px; color: #64748b; }
        .zt-f-back .badge { background: #6366f1; color: #fff; }
        .zt-f-card p { font-size: 1.15rem; font-weight: 800; line-height: 1.6; margin: 0; }
        .zt-f-front .hint { position: absolute; bottom: 25px; color: #cbd5e1; font-size: 0.8rem; font-weight: 700; display: flex; align-items: center; gap: 8px; }

        /* Actions */
        .zt-share-actions { text-align: center; padding: 40px; background: #f8fafc; border-radius: 32px; }
        .zt-share-actions .lab { font-weight: 900; color: #0f172a; margin-bottom: 25px; font-size: 1.1rem; }
        .zt-share-actions .btns { display: flex; justify-content: center; gap: 15px; }
        .zt-share-actions button { border: none; padding: 12px 30px; border-radius: 14px; font-family: inherit; font-weight: 900; color: #fff; cursor: pointer; transition: 0.3s; display: flex; align-items: center; gap: 10px; }
        .sh-whatsapp { background: #25d366; }
        .sh-facebook { background: #1877f2; }
        .sh-linkedin { background: #0a66c2; }
        .zt-share-actions button:hover { transform: translateY(-3px); box-shadow: 0 10px 20px rgba(0,0,0,0.1); }

        @media(max-width:1100px){
          .zt-points-grid { grid-template-columns: 1fr; }
          .zt-flashcards-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media(max-width:768px){
          .zt-root { padding: 60px 0 80px; }
          .zt-container { padding: 0 16px; }
          .zt-title { font-size: clamp(1.8rem, 6vw, 2.6rem); }
          .zt-hero-card { padding: 30px 20px; border-radius: 24px; }
          .zt-hero-content { flex-direction: column; text-align: center; gap: 30px; }
          .zt-feat-pills { flex-wrap: wrap; justify-content: center; }
          
          .zt-uploader-box { padding: 30px 20px; border-radius: 24px; }
          .zt-main-btn { padding: 15px; font-size: 1.1rem; }
          
          .zt-summary-panel { padding: 30px 20px; border-radius: 24px; }
          .zt-panel-header { flex-direction: column; text-align: center; gap: 15px; }
          .zt-reset-btn { width: 100%; justify-content: center; }
          
          .zt-point-card { padding: 20px; gap: 15px; }
          .zt-flashcards-grid { grid-template-columns: 1fr; }
          .zt-f-front, .zt-f-back { padding: 30px 20px; }
          
          .zt-share-actions { padding: 30px 20px; border-radius: 24px; }
          .zt-share-actions .btns { flex-direction: column; }
          .zt-share-actions button { justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default ZatounaDashboard;
